/**
 * Organización del hilo de mensajes de un ticket (lógica pura): separadores
 * por día y agrupación de mensajes consecutivos del mismo autor.
 */

/** Ventana en la que mensajes seguidos del mismo autor se agrupan. */
export const GROUP_WINDOW_MS = 10 * 60 * 1000

/** Filtros del hilo. `internal` sólo aplica a agentes de soporte. */
export const THREAD_FILTERS = {
  all: () => true,
  public: (c) => !c.internal,
  internal: (c) => c.internal === true,
}

/** Clave local AAAA-MM-DD de una fecha (para agrupar por día). */
const dayKey = (date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`

/**
 * Etiqueta de un día relativa a `now`: "Hoy", "Ayer" o la fecha.
 * @param {Date} date
 * @param {Date} now
 * @returns {string}
 */
export const dayLabel = (date, now) => {
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (dayKey(date) === dayKey(now)) return 'Hoy'
  if (dayKey(date) === dayKey(yesterday)) return 'Ayer'
  return date.toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  })
}

/**
 * Convierte los comentarios en la secuencia a pintar: separadores de día y
 * mensajes. `startsGroup` indica si el mensaje muestra autor y avatar (primer
 * mensaje de un grupo del mismo autor, tipo y día, dentro de la ventana).
 *
 * @param {Array<Object>} comments - Con id, userId, createdAt, internal.
 * @param {Object} [options]
 * @param {Date} [options.now=new Date()]
 * @returns {Array<{kind: 'day', key: string, label: string} |
 *   {kind: 'message', key: string, comment: Object, startsGroup: boolean}>}
 */
export const buildThread = (comments = [], { now = new Date() } = {}) => {
  const sorted = [...comments].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  )
  const items = []
  let prev = null

  for (const comment of sorted) {
    const date = new Date(comment.createdAt)
    const newDay = !prev || dayKey(date) !== dayKey(new Date(prev.createdAt))
    if (newDay) {
      items.push({
        kind: 'day',
        key: `day-${dayKey(date)}`,
        label: dayLabel(date, now),
      })
    }
    const startsGroup =
      newDay ||
      prev.userId !== comment.userId ||
      Boolean(prev.internal) !== Boolean(comment.internal) ||
      date - new Date(prev.createdAt) > GROUP_WINDOW_MS

    items.push({
      kind: 'message',
      key: `msg-${comment.id ?? comment.createdAt}`,
      comment,
      startsGroup,
    })
    prev = comment
  }
  return items
}
